<script lang="ts">
  import { onMount } from 'svelte';
  import { currentView, clients, stages, needsOnboarding, userEmail as userEmailStore, agentName as agentNameStore, agentTitle as agentTitleStore, georgeUnread, userTier, userActive, userDaysRemaining, subscriptionEndsAt, isAdmin, orgPricing, setThemeFromServer, appTheme, saveTheme, authProvider, pinnedClients, fundPins, pollingActive, checkExistingPoll, lastTopLevelView, LEGACY_TEACH_TABS, stageColor, fundStageColor, refreshStages, setStagesSnapshot, setVocabulary, stageLabel, terms } from './lib/stores';
  import { getFundPins } from './lib/api/fund-ir';
  import { getPinnedClients } from './lib/api';
  import { getOnboardingStatus, resetOnboarding, wipeAndRebuild, getBusinessConfig, getGeorgeUnreadCount, getPersonas, type PersonasData } from './lib/api';
  import { request } from './lib/api/client';
  import { pollVersion, startSync, clearCachedUserData } from './lib/stores';
  import Today from './pages/Today.svelte';
  import Dashboard from './pages/Dashboard.svelte';
  import Contacts from './pages/Contacts.svelte';
  import TeachGeorge from './pages/TeachGeorge.svelte';
  import Prospecting from './pages/Prospecting.svelte';
  import Sequences from './pages/Sequences.svelte';
  import Settings from './pages/Settings.svelte';
  import George from './pages/George.svelte';
  import Admin from './pages/Admin.svelte';
  import SuperAdmin from './pages/SuperAdmin.svelte';
  import Scheduled from './pages/Scheduled.svelte';
  import ClientView from './pages/ClientView.svelte';
  import Login from './pages/Login.svelte';
  import Onboarding from './pages/Onboarding.svelte';
  import FundWorkspace from './pages/FundWorkspace.svelte';
  import SidebarNavItem from './lib/SidebarNavItem.svelte';
  import Paywall from './pages/Paywall.svelte';

  const BASE = '/api';

  let authed = $state<boolean | null>(null); // null = checking
  let userEmail = $state('');
  let userName = $state('');
  let onboarding = $state(false);
  let onboardingMode = $state<'full' | 'user'>('full');
  let workflow = $state<'sales' | 'fund'>('sales');
  let accountTypeLocked = $state(false);
  let onboardingStatusError = $state(false);
  let switchingOrgType = $state(false);
  let orgTypeSwitchError = $state('');

  // Below 768px the sidebar stops being a flex column and becomes a fixed overlay
  // (app.css: `position: fixed; z-index: 30`), so leaving it open buries the page
  // under it. Start collapsed if we load there — no flash, because from the user's
  // side nothing just happened.
  const _narrowMQ = () => (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia('(max-width: 768px)') : null;
  let sidebarOpen = $state(!(_narrowMQ()?.matches ?? false));
  // Drives a brief solid-accent pulse on the reopen grip. Auto-hiding a panel the
  // user did not touch is otherwise indistinguishable from it vanishing — the pulse
  // says where it went, on the control that brings it back.
  let sidebarFlash = $state(false);
  let _flashTimer: ReturnType<typeof setTimeout> | undefined;
  // True only while the sidebar is closed BECAUSE the viewport narrowed. It is what
  // makes the restore safe: widening reopens only what the zoom took away, never a
  // panel you closed on purpose. Any manual toggle clears it — your click owns the
  // state from then on. Plain `let`, not `$state`: nothing renders it.
  let _autoHidden = false;

  $effect(() => {
    const mq = _narrowMQ();
    if (!mq) return;
    // NOTE: `sidebarOpen` is read only inside the callback, never during this
    // effect's synchronous run — otherwise the effect would depend on it and
    // re-register the listener on every toggle.
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        if (!sidebarOpen) return;              // already closed — nothing to announce
        sidebarOpen = false;
        _autoHidden = true;
        sidebarFlash = true;
        clearTimeout(_flashTimer);
        _flashTimer = setTimeout(() => { sidebarFlash = false; }, 1200);
      } else {
        // Widened again (zoom out, window resize): put back only what we took.
        if (!_autoHidden || sidebarOpen) return;
        _autoHidden = false;
        sidebarOpen = true;
        // No pulse on the way back — the panel reappearing is its own signal.
        sidebarFlash = false;
        clearTimeout(_flashTimer);
      }
    };
    mq.addEventListener('change', onChange);
    return () => { mq.removeEventListener('change', onChange); clearTimeout(_flashTimer); };
  });


  // The AI ASSISTANT's name (George, Mike, …) — the Today greeting, the sidebar
  // "Teach …". NOT the active email-writing persona, which may be
  // the user's own "(Human)"/"(Emulate)" voice while the assistant keeps its name.
  // Fund tenants name their agent in the wizard too, so both branches resolve here.
  function resolveAgentName(data: PersonasData | null, fallback = ''): string {
    const personas = data?.personas || {};
    const isHuman = (p?: { label?: string }) => /\((Human|Emulate)\)/i.test(p?.label || '');
    let agent = data?.active ? personas[data.active] : null;
    if (!agent || isHuman(agent)) agent = Object.values(personas).find(p => !isHuman(p)) || agent;
    return agent?.label?.replace(/\s*\(Human\)|\s*\(Emulate\)/g, '') || fallback;
  }

  onMount(async () => {
    // Check if already authenticated
    try {
      const res = await fetch(`${BASE}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        userEmail = data.email;
        $userEmailStore = data.email;
        setThemeFromServer(data.theme || 'light');
        userName = data.name || '';
        $userTier = data.tier ?? 0;
        $userActive = data.active ?? true;
        $userDaysRemaining = data.days_remaining ?? null;
        $subscriptionEndsAt = data.subscription_ends_at ?? '';
        $isAdmin = data.is_admin ?? false;
        if (data.pricing) $orgPricing = data.pricing;
        $authProvider = data.auth_provider === 'microsoft' ? 'microsoft' : 'google';
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          // ?reset_onboarding=1 wipes onboarding state. The comment always said "dev
          // mode" but the check did not enforce it, so the URL worked in production.
          if (import.meta.env.DEV && params.get('reset_onboarding') === '1') {
            await resetOnboarding();
            window.history.replaceState({}, '', window.location.pathname);
          }
          // Stripe return: strip billing param and re-fetch user data
          if (params.get('billing') === 'success' || params.get('billing') === 'cancelled') {
            window.history.replaceState({}, '', window.location.pathname);
            // Re-fetch /auth/me to pick up tier change from webhook
            const refresh = await fetch(`${BASE}/auth/me`, { credentials: 'include' });
            if (refresh.ok) {
              const d = await refresh.json();
              $userTier = d.tier ?? 0;
              $userActive = d.active ?? true;
              $userDaysRemaining = d.days_remaining ?? null;
              $subscriptionEndsAt = d.subscription_ends_at ?? '';
              if (d.pricing) $orgPricing = d.pricing;
            }
          }
          // When the user returns to the tab (e.g. after resuming/cancelling in the
          // Stripe portal opened in another tab), re-pull billing state so the banner
          // and tier reflect any webhook-driven changes.
          document.addEventListener('visibilitychange', async () => {
            if (document.visibilityState !== 'visible') return;
            try {
              const r = await fetch(`${BASE}/auth/me`, { credentials: 'include' });
              if (r.status === 401) {
                clearCachedUserData();
                setStagesSnapshot({});
                authed = false;
                return;
              }
              if (!r.ok) return;
              if (workflow !== 'fund') refreshStages().catch(() => {});
              const d = await r.json();
              $userTier = d.tier ?? 0;
              $userActive = d.active ?? true;
              $userDaysRemaining = d.days_remaining ?? null;
              $subscriptionEndsAt = d.subscription_ends_at ?? '';
              if (canSwitchOrgType) {
                const latest = await getOnboardingStatus();
                if (latest.workflow !== workflow) {
                  clearCachedUserData();
                  setStagesSnapshot({});
                  window.location.reload();
                  return;
                }
              }
            } catch {}
          });
          window.addEventListener('storage', (event) => {
            if (canSwitchOrgType && event.key === 'internal-org-type-switch') {
              window.location.reload();
            }
          });
        }
        // If the org's trial has expired / not active, skip onboarding and render paywall
        if (!$userActive) {
          authed = true;
          return;
        }
        // Check onboarding status. On a successful response we honour
        // the server's verdict. On a network/5xx failure we DO NOT drop
        // the user into a fresh onboarding — the user is already
        // authenticated via /auth/me, which is the much stronger signal
        // that they've been here before; transient API lag must not
        // re-force the onboarding wizard. The endpoint is idempotent,
        // so the next page refresh will recover the real state cleanly.
        try {
          const ob = await getOnboardingStatus();
          workflow = ob.workflow;
          accountTypeLocked = ob.account_type_locked;
          // Set the vocabulary before anything paints. This is the only call
          // that supplies it, so an early return before this line leaves the
          // whole shell reading Sales words at an IR account.
          setVocabulary(ob.vocabulary, ob.workflow);
          if (!ob.completed) {
            onboarding = true;
            onboardingMode = ob.company_setup ? 'user' : 'full';
            $needsOnboarding = true;
            if (ob.user_name) userName = ob.user_name;
            authed = true;
            return;
          }
          if (ob.workflow === 'fund') {
            const [fundPersonas, fundPinned] = await Promise.all([
              getPersonas().catch(() => null),
              getFundPins().catch(() => ({ pinned: [] })),
            ]);
            const fundAgent = resolveAgentName(fundPersonas, 'Dealton');
            $agentNameStore = fundAgent;
            $fundPins = fundPinned.pinned || [];
            const requested = viewFromPath(window.location.pathname);
            const fundView = fundAllowedView(requested) ? requested : 'today';
            suppressUrlSync = true;
            $currentView = fundView;
            suppressUrlSync = false;
            history.replaceState({ view: fundView }, '', canonicalPath(fundView));
            urlSyncReady = true;
            authed = true;
            checkExistingPoll();
            return;
          }
        } catch (e) {
          console.warn('getOnboardingStatus failed — workflow is unknown', e);
          onboardingStatusError = true;
          authed = true;
          return;
        }
        const [, biz, unread, personasData, pins] = await Promise.all([
          // Every call here is caught: a transient failure must degrade the page,
          // not fall through to the outer catch, which renders <Login /> at an
          // authenticated user. Same reasoning as getOnboardingStatus above.
          refreshStages().catch(() => ({} as Record<string, string>)),
          getBusinessConfig().catch(() => null),
          getGeorgeUnreadCount().catch(() => ({ count: 0 })),
          getPersonas().catch(() => null),
          getPinnedClients().catch(() => ({ pinned: [] as string[] })),
        ]);
        // refreshStages already applied the newest server snapshot.
        $pinnedClients = pins.pinned || [];
        const salesAgent = resolveAgentName(personasData);
        if (salesAgent) {
          $agentNameStore = salesAgent;
        }
        if (biz?.ai_assistant_expertise) {
          $agentTitleStore = biz.ai_assistant_expertise;
        }
        $georgeUnread = unread.count;
        initViewFromUrl();
        // Mount the requested page only after its route and shared stage data
        // are ready. Otherwise a client deep link first mounts Today, fires its
        // requests, then replaces the whole page with ClientView.
        authed = true;
        checkExistingPoll();
      } else {
        if (res.status === 401) {
          clearCachedUserData();
          setStagesSnapshot({});
        }
        authed = false;
      }
    } catch {
      authed = false;
    }
  });

  async function logout() {
    // Clear BEFORE the request: this is local and can't fail, the fetch can.
    // Clearing afterwards means a network error leaves the previous user's
    // drafts and contacts in localStorage. See stores.ts:clearCachedUserData.
    clearCachedUserData();
    setStagesSnapshot({});
    await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    authed = false;
  }

  const stageEmoji: Record<string, string> = {
    'Unevaluated': '⚪', 'Prospect': '⚪', 'Top of Funnel': '🔵', 'Engaged': '🟡',
    'Active Deal': '🟠', 'Client': '🟢', 'On Hold': '⏸️',
  };

  // Pinned-client stage dot. Small (6px) and sitting in the same 16px glyph
  // column the nav icons use, so it fills the indent gutter instead of leaving
  // a hole, and the client names stay on the nav labels' 49px text axis.
  // The row carries `title`/`aria-label` with the stage WORD — a bare colour
  // dot would be colour-alone encoding, which §1 forbids.

  const HIDE_FROM_NAV = new Set(['Prospect', 'Non-Clients']);
  const TOF_SIDEBAR_LIMIT = 200;
  const tofCount = $derived($clients.filter(c => $stages[c] === 'Top of Funnel').length);
  const showTof = $derived(tofCount < TOF_SIDEBAR_LIMIT);

  /** Dealton staff. The ONE expression for it in this component — the Super
   *  Admin rail and the Settings danger zone both read it, so they cannot
   *  drift apart. Mirrors `super_admin.is_dealton_staff` on the backend, which
   *  is where the actual authorization lives. */
  const isSuperadmin = $derived(userEmail.toLowerCase().endsWith('@dealton.ai'));
  const canSwitchOrgType = $derived(isSuperadmin || userEmail.toLowerCase().endsWith('@geomet.ai'));

  async function switchOrgType() {
    if (!canSwitchOrgType || switchingOrgType) return;
    const next = workflow === 'fund' ? 'corporate' : 'fund';
    const nextLabel = next === 'fund' ? 'Fund / Investor Relations' : 'Corporate / Sales';
    if (!confirm(`Switch the whole organization to ${nextLabel}? Every member will see the new workflow. An active mailbox sync will block the change. Saved Sales and Fund data will remain.`)) return;
    switchingOrgType = true;
    orgTypeSwitchError = '';
    try {
      await request('/internal/org-type', {
        method: 'POST', body: JSON.stringify({ account_type: next }),
      });
      clearCachedUserData();
      setStagesSnapshot({});
      try { localStorage.setItem('internal-org-type-switch', String(Date.now())); } catch {}
      window.location.assign('/today');
    } catch (e: any) {
      orgTypeSwitchError = e?.message || 'Could not switch organization type';
      switchingOrgType = false;
    }
  }

  // Known page views (non-client)
  const pages = new Set(['today', 'dashboard', 'contacts', 'teach', 'prospecting', 'sequences', 'george', 'settings', 'scheduled', 'admin', 'super-admin', 'onboarding-preview']);

  function fundAllowedView(view: string): boolean {
    return ['today', 'dashboard', 'contacts', 'scheduled', 'teach', 'settings'].includes(view)
      || (view === 'admin' && $isAdmin)
      || (view === 'super-admin' && isSuperadmin)
      || /^org:(?:institution:[a-f0-9]{32}|domain:[a-z0-9.-]{1,253})$/.test(view)
      || /^[a-f0-9]{32}$/.test(view);
  }

  // URL aliases — internal view name -> external URL slug.
  // The 'george' view is exposed to users as /voice (Voice panel).
  const viewToUrlSlug: Record<string, string> = { george: 'voice' };
  const urlSlugToView: Record<string, string> = { voice: 'george' };

  function canonicalPath(view: string): string {
    const slug = viewToUrlSlug[view] ?? view;
    if (view.startsWith('org:')) return `/fund/organizations/${encodeURIComponent(view.slice(4))}`;
    return pages.has(view) ? `/${slug}` : `/clients/${view}`;
  }


  // Sync URL whenever currentView changes (from anywhere — sidebar, dashboard, etc.)
  let suppressUrlSync = false;
  // Svelte stores fire their subscribers immediately with the current value.
  // The currentView store defaults to 'today', so the very first subscribe
  // tick (which runs at module-load time, BEFORE initViewFromUrl in onMount)
  // would pushState to '/today' and obliterate any deep link like
  // /clients/Foo the user opened the SPA on. Wait until initViewFromUrl has
  // primed currentView from the path, then start writing the URL.
  let urlSyncReady = false;
  // Track the prior top-level page so a client view can render
  // "← Back to {prior}" instead of a context-free "Back". Updated only
  // when the OLD value was a known page name; landing on a client never
  // overwrites the saved value.
  let _priorView = '';
  currentView.subscribe(view => {
    if (urlSyncReady && _priorView && pages.has(_priorView) && !pages.has(view)) {
      lastTopLevelView.set(_priorView);
    }
    _priorView = view;
    if (!urlSyncReady || suppressUrlSync || typeof window === 'undefined') return;
    const path = canonicalPath(view);
    if (window.location.pathname !== path) {
      history.pushState({ view }, '', path);
    }
  });

  function navTo(view: string) {
    if (workflow === 'fund' && !fundAllowedView(view)) return;
    $currentView = view;
  }

  function handleNavKey(e: KeyboardEvent, view: string) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navTo(view);
    }
  }

  // Read initial view from URL. Bare `/` lands on Today (the default
  // landing view); a known slug routes to that page; anything else
  // (including unknown paths) falls back to Today as well so a stale or
  // malformed URL still gives the user a usable home rather than a
  // surprise Dashboard.
  function viewFromPath(path: string): string {
    if (path.startsWith('/fund/organizations/')) {
      try { return `org:${decodeURIComponent(path.slice('/fund/organizations/'.length))}`; }
      catch { return 'today'; }
    }
    if (path.startsWith('/clients/')) return path.slice('/clients/'.length);
    const slug = path.slice(1); // strip leading /
    // Retired top-level pages -> the equivalent Teach George tab. Rewrite the
    // URL here so TeachGeorge reads the right ?tab= when it mounts.
    const legacyTab = LEGACY_TEACH_TABS[slug];
    if (legacyTab) {
      if (typeof window !== 'undefined') {
        history.replaceState({ view: 'teach' }, '', `/teach?tab=${legacyTab}`);
      }
      return 'teach';
    }
    // External URL slugs may map to internal view names (e.g. /voice -> george)
    const aliased = urlSlugToView[slug];
    if (aliased && pages.has(aliased)) return aliased;
    if (pages.has(slug)) return slug;
    return 'today';
  }

  // Set initial view from URL (called after auth). Flips the
  // `urlSyncReady` flag at the end so subsequent currentView mutations
  // (sidebar clicks, decision actions) start writing the URL back. Note:
  // we mark ready even when the path was already aligned with currentView
  // — the gate is about reaching the "initial URL read" milestone, not
  // about whether the URL changed.
  function initViewFromUrl() {
    const view = viewFromPath(window.location.pathname);
    if (view !== $currentView) {
      suppressUrlSync = true;
      $currentView = view;
      suppressUrlSync = false;
    }
    urlSyncReady = true;
    // If the URL is not yet at the canonical slug for this view (e.g.
    // visiting `/` lands on Today but viewFromPath('/') === 'today' so the
    // store doesn't change and the subscriber never fires), push the
    // canonical path now so the address bar matches the rendered page.
    const canonical = canonicalPath(view);
    if (window.location.pathname !== canonical) {
      history.replaceState({ view }, '', canonical);
    }
  }

  // Handle browser back/forward
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', (e) => {
      if (workflow === 'fund') {
        const requested = viewFromPath(window.location.pathname);
        const fundView = fundAllowedView(requested) ? requested : 'today';
        suppressUrlSync = true;
        $currentView = fundView;
        suppressUrlSync = false;
        const canonical = canonicalPath(fundView);
        if (window.location.pathname !== canonical) history.replaceState({ view: fundView }, '', canonical);
        return;
      }
      const view = e.state?.view || viewFromPath(window.location.pathname);
      suppressUrlSync = true;
      $currentView = view;
      suppressUrlSync = false;
    });
  }
</script>

{#if authed === null}
  <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:var(--bg);color:var(--text-dim);font-size:13px;gap:8px;"><span class="spinner"></span> Loading...</div>
{:else if !authed}
  <Login />
{:else if onboardingStatusError}
  <div style="min-height:100vh;display:grid;place-items:center;background:var(--bg);color:var(--text);">
    <div style="max-width:420px;padding:28px;"><h1>We could not load your workspace</h1><p>Please try again. Your account data has not changed.</p><button onclick={() => window.location.reload()}>Retry</button></div>
  </div>
{:else if $currentView === 'onboarding-preview'}
  <!-- Replay of the real wizard for an account that already finished it.
       mode is forced to 'full': a tenant with company config is routed to the
       two-step teammate wizard, where the workflow chooser never renders — so
       'user' mode could not show the thing you came here to look at. Every
       write is blocked inside Onboarding by the `preview` prop. -->
  <Onboarding userName={userName} mode="full" preview
    onComplete={() => {}} onExit={() => navTo('today')} />
{:else if onboarding}
  {#if canSwitchOrgType}
    <div style="position:fixed;top:12px;right:16px;z-index:1000;display:grid;gap:6px;max-width:260px;">
      <button type="button" onclick={switchOrgType} disabled={switchingOrgType}
        style="padding:8px 12px;border:1px solid var(--border);border-radius:8px;background:var(--bg-surface);color:var(--text);cursor:pointer;">
        {switchingOrgType ? 'Switching…' : `Switch org to ${workflow === 'fund' ? 'Sales' : 'Fund / IR'}`}
      </button>
      {#if orgTypeSwitchError}<div role="alert" style="padding:8px;background:var(--bg-surface);color:var(--red-ink);">{orgTypeSwitchError}</div>{/if}
    </div>
  {/if}
  <Onboarding userName={userName} mode={onboardingMode} initialWorkflow={workflow} {accountTypeLocked} onComplete={async (chosenWorkflow) => {
    workflow = chosenWorkflow;
    if (chosenWorkflow === 'fund') {
      const fundAgent = resolveAgentName(await getPersonas().catch(() => null), 'Dealton');
      $agentNameStore = fundAgent;
    }
    try {
      const status = await getOnboardingStatus();
      setVocabulary(status.vocabulary, status.workflow);
    } catch {
      // Completion is already saved. Preserve Finish → sync even when a
      // transient status read fails, and show the existing retry screen.
      onboarding = false;
      $needsOnboarding = false;
      onboardingStatusError = true;
      startSync(true);
      return;
    }
    onboarding = false;
    $needsOnboarding = false;
    if (chosenWorkflow === 'sales') {
      await refreshStages();
    }
    navTo('today');
    startSync(true);
  }} />
{:else if !$userActive}
  <Paywall onLogout={logout} />
{:else}
<div class="layout">
  <aside class="sidebar" class:collapsed={!sidebarOpen}>
    <div class="sidebar-header" style="display:flex;align-items:center;justify-content:space-between;">
      <span style="display:flex; align-items:center; gap:6px; cursor:pointer;" onclick={() => navTo('today')}>
        <img src={$appTheme === 'dark' ? '/mark-dark.svg' : '/mark.svg'} alt="" style="height:28px; width:28px;" />
        <span style="font-size:22px; font-weight:700; letter-spacing:-0.5px;"><span style="color:#e8553a;">deal</span><span style="color:var(--text);">ton</span></span>
        {#if workflow === 'fund'}
          <span class="workspace-badge" title="Dealton Finance">finance</span>
        {/if}
      </span>
      <button type="button" class="sidebar-grip" onclick={() => { sidebarOpen = false; _autoHidden = false; }}
        aria-expanded="true" title="Hide sidebar" aria-label="Hide sidebar"
        ><svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg></button>
    </div>

    <!-- WORK -->
    <div class="sidebar-section">
      <div class="sidebar-label">Work</div>
      <SidebarNavItem icon="today" label="Today" active={$currentView === 'today'} onNavigate={() => navTo('today')} />
      <SidebarNavItem icon="dashboard" label="Dashboard" active={$currentView === 'dashboard'} onNavigate={() => navTo('dashboard')} />
      <SidebarNavItem icon="contacts" label="Contacts" active={$currentView === 'contacts'} onNavigate={() => navTo('contacts')} />
      <!-- No Scheduled item: Today's "Going out soon" IS this queue (first 5, cancel
           inline), so a permanent entry duplicates a section that is already there and
           reads empty whenever the queue is. The page keeps its URL -- see `pages`. -->
      {#if workflow !== 'fund'}
      <div class="sidebar-item" class:active={$currentView === 'prospecting'}
        role="button" tabindex="0"
        onclick={() => navTo('prospecting')}
        onkeydown={(e) => handleNavKey(e, 'prospecting')}>
        <span class="nav-glyph"><svg viewBox="0 0 24 24"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg></span>{$terms.nav_prospecting}
      </div>
      <div class="sidebar-item" class:active={$currentView === 'george'}
        role="button" tabindex="0"
        onclick={() => navTo('george')}
        onkeydown={(e) => handleNavKey(e, 'george')}>
        <span class="nav-glyph"><svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z"/></svg></span>Live Calls
      </div>
      {/if}
    </div>

    <!-- PINNED CLIENTS -->
    <div class="sidebar-section" style="flex:1; overflow:hidden; display:flex; flex-direction:column;">
      <div class="sidebar-label">{$terms.nav_pinned}</div>
      <div class="sidebar-clients-scroll">
        {#if workflow === 'fund'}
          {#if $fundPins.length === 0}<div class="sidebar-empty">Pin an investor from their relationship page.</div>
          {:else}
            {#each $fundPins as pin (pin.entity_key || pin.counterparty_id)}
              {@const target = pin.entry_contact_id || pin.counterparty_id || (pin.entity_key ? `org:${pin.entity_key}` : 'dashboard')}
              <button type="button" class="sidebar-item sidebar-client" class:active={$currentView === target}
                      title={pin.stage ? `${pin.display_name} — ${pin.stage.replaceAll('_', ' ')}` : pin.display_name}
                      onclick={() => navTo(target)}>
                <!-- fundStageColor, not stageColor: these are Fund stage keys
                     ('active_lp', …) and the Sales lookup misses every one of them, so
                     every pin came out the same fallback grey while the dashboard beside
                     it showed real stage colours. -->
                <span class="nav-glyph" aria-hidden="true"><span class="client-dot" style="background:{fundStageColor(pin.stage || '')};"></span></span>
                <span class="truncate">{pin.display_name}</span>
              </button>
            {/each}
          {/if}
        {:else}
        {#if $pinnedClients.length === 0}
          <div class="sidebar-empty">{$terms.pin_hint}</div>
        {:else}
          {#each $pinnedClients as client}
            {@const stage = $stages[client] || ''}
            <div class="sidebar-item sidebar-client" class:active={$currentView === client}
              role="button" tabindex="0"
              title={stage ? `${client.replace(/_/g, ' ')} — ${$stageLabel(stage)}` : client.replace(/_/g, ' ')}
              aria-label={stage ? `${client.replace(/_/g, ' ')}, ${$stageLabel(stage)}` : client.replace(/_/g, ' ')}
              onclick={() => navTo(client)}
              onkeydown={(e) => handleNavKey(e, client)}>
              <span class="nav-glyph" aria-hidden="true">
                <span class="client-dot" style="background:{stageColor(stage)};"></span>
              </span>
              <span class="truncate">{client.replace(/_/g, ' ')}</span>
            </div>
          {/each}
        {/if}
        {/if}
      </div>
    </div>

    <div class="sidebar-bottom">
      <!-- Teach <Name> — a peer of Settings, not a section of its own. Its
           former two children (Product Knowledge, Collateral) are now tabs. -->
      <SidebarNavItem icon="teach" label="Teach {$agentNameStore ? $agentNameStore.split(' ')[0] : 'Dealton'}" active={$currentView === 'teach'} onNavigate={() => navTo('teach')} />
      <SidebarNavItem icon="settings" label="Settings" active={$currentView === 'settings'} onNavigate={() => navTo('settings')} />
      {#if canSwitchOrgType}
        <button type="button" class="sidebar-item" onclick={switchOrgType} disabled={switchingOrgType}
          title="Internal testing · switches the whole organization without deleting data">
          <span class="nav-glyph" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4 7h15m-4-4 4 4-4 4M20 17H5m4-4-4 4 4 4"/></svg></span>
          {switchingOrgType ? 'Switching…' : `Switch org to ${workflow === 'fund' ? 'Sales' : 'Fund / IR'}`}
        </button>
        {#if orgTypeSwitchError}<div role="alert" class="sidebar-empty">{orgTypeSwitchError}</div>{/if}
      {/if}
      {#if $isAdmin}
      <div class="sidebar-item" class:active={$currentView === 'admin'}
        role="button" tabindex="0"
        onclick={() => navTo('admin')}
        onkeydown={(e) => handleNavKey(e, 'admin')}>
        <span class="nav-glyph"><svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>Admin
      </div>
      {/if}
      {#if isSuperadmin}
      <div class="sidebar-item" class:active={$currentView === 'super-admin'}
        role="button" tabindex="0"
        onclick={() => navTo('super-admin')}
        onkeydown={(e) => handleNavKey(e, 'super-admin')}>
        <span class="nav-glyph"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/></svg></span>Super Admin
      </div>
      {/if}
    </div>
  </aside>

  <main class="main">
    {#if !sidebarOpen}
      <button type="button" class="sidebar-grip reopen" class:flash={sidebarFlash}
        onclick={() => { sidebarOpen = true; sidebarFlash = false; _autoHidden = false; }}
        aria-expanded="false" title="Show sidebar" aria-label="Show sidebar"
        ><svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg></button>
    {/if}
    <div class="main-content">
    {#if workflow === 'fund'}
      {#if $currentView === 'super-admin' && isSuperadmin}
        <SuperAdmin />
      {:else if $currentView === 'admin' && $isAdmin}
        <Admin workflow="fund" />
      {:else if $currentView === 'teach'}
        <TeachGeorge />
      {:else if $currentView === 'settings'}
        <Settings workflow="fund" onLogout={logout} />
      {:else}
        <FundWorkspace view={['today', 'dashboard', 'contacts', 'scheduled'].includes($currentView)
          ? $currentView as 'today' | 'dashboard' | 'contacts' | 'scheduled' : 'detail'}
          contactId={$currentView} {userName} />
      {/if}
    {:else if $currentView === 'today'}
      <Today />
    {:else if $currentView === 'dashboard'}
      <Dashboard />
    {:else if $currentView === 'contacts'}
      <Contacts />
    {:else if $currentView === 'teach'}
      <TeachGeorge />
    {:else if $currentView === 'prospecting'}
      <Prospecting />
    {:else if $currentView === 'sequences'}
      <Sequences />
    {:else if $currentView === 'george'}
      <George />
    {:else if $currentView === 'settings'}
      <!-- Destructive data controls, for Dealton staff and in dev.
           🔴 This is NOT authorization — it decides what renders, nothing more.
           `POST /api/poller/wipe` and `POST /api/onboarding/reset` enforce their
           own gates, and `/poller/wipe` only acquired one when this block came
           back (it had none before: it was unreachable because the markup was
           compiled out, which is not the same thing as being protected).
           Same predicate as the Super Admin rail below — change one, change both. -->
      {#if isSuperadmin || import.meta.env.DEV}
        <Settings
          canWipe
          onWipeData={async () => {
            if ($pollingActive) { alert('A sync is running — wait for it to finish.'); return; }
            if (!confirm('Wipe email data only?')) return;
            try {
              await wipeAndRebuild();
            } catch (e: any) {
              // `request()` throws on a non-2xx, and with no catch that is an
              // unhandled rejection: the button appears to do nothing. For a
              // destructive control, silence is the worst possible feedback.
              alert(`Wipe refused: ${e?.message || 'request failed'}`);
              return;
            }
            setStagesSnapshot({}); $pollVersion = $pollVersion + 1;
            alert('Wipe complete');
          }}
          onWipeOnboard={async () => {
            if ($pollingActive) { alert('A sync is running — wait for it to finish.'); return; }
            if (!confirm('Wipe ALL data and restart onboarding?')) return;
            try {
              await wipeAndRebuild();
              // 🔴 This one is org-admin-only, and Dealton staff are not all
              // admins — so it can 403 for an account that just passed the wipe
              // gate, leaving the data wiped and no wizard. That asymmetry is
              // why the message below names the fix.
              await resetOnboarding();
            } catch (e: any) {
              alert(`Could not finish: ${e?.message || 'request failed'}\n\n`
                + 'Restarting onboarding needs an org admin (Admin → Users). '
                + 'Any email data already wiped will re-ingest on the next sync.');
              setStagesSnapshot({}); $pollVersion = $pollVersion + 1;
              return;
            }
            onboarding = true; onboardingMode = 'full'; $needsOnboarding = true;
            setStagesSnapshot({}); $pollVersion = $pollVersion + 1;
          }}
        />
      {:else}
        <Settings />
      {/if}
    {:else if $currentView === 'admin'}
      <Admin />
    {:else if $currentView === 'super-admin'}
      <SuperAdmin />
    {:else if $currentView === 'scheduled'}
      <Scheduled />
    {:else}
      <ClientView client={$currentView} />
    {/if}
    </div>
  </main>
</div>
{/if}
